import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  Bell, Calendar, Check, X, Star, CreditCard, Package, Truck,
  Store, MessageCircle, Gift, Share2, Sparkles, Link as LinkIcon,
  ChevronRight, CheckCheck,
} from 'lucide-react';

const ICON_MAP = {
  calendar: Calendar,
  check: Check,
  x: X,
  star: Star,
  'credit-card': CreditCard,
  package: Package,
  truck: Truck,
  store: Store,
  'message-circle': MessageCircle,
  gift: Gift,
  share: Share2,
  sparkles: Sparkles,
  link: LinkIcon,
  bell: Bell,
};

const TYPE_TO_ICON = {
  booking_reminder: 'calendar',
  booking_confirmed: 'check',
  booking_cancelled: 'x',
  booking_completed: 'star',
  booking_paid: 'credit-card',
  order_placed: 'package',
  order_shipped: 'truck',
  order_delivered: 'check',
  order_ready: 'store',
  message: 'message-circle',
  rating: 'star',
  system: 'bell',
  cart_gifted: 'gift',
  cart_shared: 'share',
  maintenance_paid: 'sparkles',
  maintenance_pay_request: 'link',
  checkout_gifted: 'gift',
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const ms = ts.toMillis?.() ?? new Date(ts).getTime();
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  // Group into "New" (unread) and "Earlier" (read)
  const { unread, read } = useMemo(() => {
    const u = [];
    const r = [];
    notifications.forEach(n => {
      if (n.read) r.push(n);
      else u.push(n);
    });
    return { unread: u, read: r };
  }, [notifications]);

  const handleTap = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    deleteNotification(id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-20">
      {/* Header — matches CalendarPage */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-500 mt-1">
            {notifications.length > 0
              ? unreadCount > 0
                ? `${unreadCount} new · ${notifications.length} total`
                : `${notifications.length} notification${notifications.length === 1 ? '' : 's'}`
              : 'Your updates and reminders'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="px-4 py-2 rounded-full bg-[#FFF5F6] text-[#EFA9B6] text-xs font-bold hover:bg-[#FBE1E8] transition-colors flex items-center gap-1.5 shrink-0 mt-1"
          >
            <CheckCheck size={14} />
            Mark all read
          </button>
        )}
      </div>

      {/* Empty state — matches CalendarPage */}
      {notifications.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-50">
          <div className="w-24 h-24 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell size={40} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
            No notifications yet
          </h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            We'll let you know when you get bookings, orders, or messages.
          </p>
          <button
            onClick={() => navigate('/dashboard/search')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Explore Skytone
          </button>
        </div>
      ) : (
        <>
          {/* Unread section */}
          {unread.length > 0 && (
            <div className="mb-6">
              <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 ml-1">
                New
              </h3>
              <div className="space-y-3">
                {unread.map((notif) => (
                  <NotificationCard
                    key={notif.id}
                    notif={notif}
                    onClick={() => handleTap(notif)}
                    onDelete={handleDelete}
                    isUnread
                  />
                ))}
              </div>
            </div>
          )}

          {/* Read section */}
          {read.length > 0 && (
            <div>
              <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 ml-1">
                Earlier
              </h3>
              <div className="space-y-3">
                {read.map((notif) => (
                  <NotificationCard
                    key={notif.id}
                    notif={notif}
                    onClick={() => handleTap(notif)}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
// NOTIFICATION CARD — matches BookingCard layout
// ============================================================
const NotificationCard = ({ notif, onClick, onDelete, isUnread }) => {
  const iconKey = TYPE_TO_ICON[notif.type] || 'bell';
  const Icon = ICON_MAP[iconKey] || Bell;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white p-4 rounded-3xl border border-gray-100 hover:border-[#EFA9B6] transition-colors flex items-start gap-4 group"
    >
      {/* Icon tile — like the booking image */}
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden shrink-0 ${
          isUnread ? 'bg-[#EFA9B6] text-white' : 'bg-[#FFF5F6] text-[#EFA9B6]'
        }`}
      >
        <Icon size={22} />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        {/* Badge row — like status badges on BookingCard */}
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          {isUnread && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFF5F6] text-[#EFA9B6]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EFA9B6]"></span>
              New
            </span>
          )}
          <span className="text-[10px] font-bold text-gray-400">
            {timeAgo(notif.createdAt)}
          </span>
        </div>

        {/* Title */}
        <h3 className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>
          {notif.title}
        </h3>

        {/* Body preview */}
        {notif.body && (
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {notif.body}
          </p>
        )}
      </div>

      {/* Right side — delete + chevron */}
      <div className="flex items-center gap-1 shrink-0 ml-2 self-center">
        <button
          onClick={(e) => onDelete(e, notif.id)}
          className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors"
          title="Delete"
        >
          <X size={16} />
        </button>
        <ChevronRight size={18} className="text-gray-300 group-hover:text-[#EFA9B6] transition-colors" />
      </div>
    </button>
  );
};

export default NotificationsPage;